import { useState } from 'react';
import { 
  X, 
  Image, 
  Calendar, 
  Clock, 
  CheckCircle,
  Warning
} from '@phosphor-icons/react';
import { 
  FacebookIcon, 
  InstagramIcon, 
  LinkedInIcon, 
  XIcon 
} from '../../../components/SocialMediaIcons';
import { validateImageFile } from '../../../services/imageService';

export default function CreatePostModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    platform: 'facebook',
    caption: '',
    image: null,
    scheduledDate: '',
    scheduledTime: '',
    status: 'Draft'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: FacebookIcon, iconId: 'facebook', color: 'bg-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: InstagramIcon, iconId: 'instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
    { id: 'linkedin', name: 'LinkedIn', icon: LinkedInIcon, iconId: 'linkedin', color: 'bg-blue-700' },
    { id: 'twitter', name: 'X', icon: XIcon, iconId: 'x', color: 'bg-black' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate the image file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setErrors(prev => ({ ...prev, image: validation.error }));
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.caption.trim()) {
      newErrors.caption = 'Caption is required';
    }
    
    if (formData.caption.length > 2200) {
      newErrors.caption = 'Caption must be less than 2200 characters';
    }

    if (formData.status === 'Scheduled' && (!formData.scheduledDate || !formData.scheduledTime)) {
      newErrors.scheduledDate = 'Scheduled date and time are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Create a new post object
      const newPost = {
        platform: formData.platform,
        platformIcon: platforms.find(p => p.id === formData.platform)?.iconId || 'facebook',
        platformColor: platforms.find(p => p.id === formData.platform)?.color || 'bg-gray-600',
        status: formData.status,
        statusColor: formData.status === 'Draft' ? 'bg-gray-400' : 
                    formData.status === 'Scheduled' ? 'bg-blue-500' : 
                    formData.status === 'Posted' ? 'bg-green-500' : 'bg-yellow-500',
        date: formData.status === 'Scheduled' 
          ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toISOString()
          : new Date().toISOString(),
        caption: formData.caption,
        image: formData.image || '' // Pass the File object directly
      };

      await onSave(newPost);
      onClose();
    } catch (error) {
      console.error('Error creating post:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to create post. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCharacterCount = () => {
    const count = formData.caption.length;
    const maxLength = 2200;
    const isNearLimit = count > maxLength * 0.9;
    const isOverLimit = count > maxLength;
    
    return (
      <div className={`text-xs ${isOverLimit ? 'text-red-500' : isNearLimit ? 'text-yellow-500' : 'text-gray-500'}`}>
        {count}/{maxLength} characters
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create New Post</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Select Platform</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => handleInputChange('platform', platform.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.platform === platform.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full ${platform.color} flex items-center justify-center text-white mx-auto mb-2`}>
                    <platform.icon size={20} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Caption */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Post Caption
            </label>
            <textarea
              value={formData.caption}
              onChange={(e) => handleInputChange('caption', e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.caption ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Write your post caption here..."
            />
            {getCharacterCount()}
            {errors.caption && (
              <p className="text-red-500 text-sm mt-1">{errors.caption}</p>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                {formData.image ? (
                  <div className="space-y-2">
                    <img 
                      src={URL.createObjectURL(formData.image)} 
                      alt="Preview" 
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <p className="text-sm text-gray-600">Click to change image</p>
                    <p className="text-xs text-gray-500">
                      {(formData.image.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Image className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-600">
                      Click to upload an image or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">JPEG, PNG, WebP up to 2MB</p>
                  </div>
                )}
              </label>
            </div>
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
          </div>

          {/* Scheduling */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Post Status
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange('status', 'Draft')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.status === 'Draft'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-1">
                    <span className="text-white text-xs">D</span>
                  </div>
                  <span className="text-sm font-medium">Draft</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange('status', 'Scheduled')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.status === 'Scheduled'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium">Scheduled</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange('status', 'Pending Approval')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.status === 'Pending Approval'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-1">
                    <Warning className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium">Pending Approval</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange('status', 'Posted')}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.status === 'Posted'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium">Posted</span>
                </div>
              </button>
            </div>
          </div>

          {/* Date and Time Selection */}
          {formData.status === 'Scheduled' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Scheduled Date
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.scheduledDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Scheduled Time
                </label>
                <input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.scheduledTime ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
            </div>
          )}

          {errors.scheduledDate && (
            <p className="text-red-500 text-sm">{errors.scheduledDate}</p>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Create Post</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
